/**
 * The reels Edit › Reels puts in its rail — every reel the contest owes, with
 * the one filling it when there is one (CAE-1552).
 *
 * **A slot for every Reel the contest owes.** `requiredReelNumbers` is the
 * contest's debt — Reels 1 to its own Reel count, three or four since CAE-1566 —
 * and the API enforces it: the backend refuses any `reelNumber` above the
 * contest's Reel count, and publish refuses a contest without exactly those
 * Reels. A rail listing only the reels the payload carries would leave an
 * operator with no way to reach the next one — and the Contest Overview's Reels
 * section pads to the same set for the same reason (CAE-1574), so the two
 * screens describe the same contest the same way one click apart.
 *
 * This is also why the handoff's full-page "No reels yet" empty state is not
 * built. It is drawn for `effectiveReels.length === 0`, which the padded rail
 * never reaches; and its `+ Add reel` CTA would set a reel count the operator
 * chooses when the Contest is created instead. A contest nobody has started on
 * gets a `not created` slot per Reel and the inline empty on whichever is
 * selected. Raised on CAE-1552.
 */
import type { ContestDetailReel } from '../../../../api/contests';
import { MIN_ENABLED_OPTIONS_PER_REEL, requiredReelNumbers } from '../../../../contests/readiness';
import { reelStats, type ReelStats } from '../../../../contests/reel-stats';

/**
 * What the rail's state dot says about a reel at a glance.
 *
 * **Two states, not three — there is no grey.** The dot was `ok | warn | empty`
 * until CAE-1591, where `empty` drew a reel nobody had created grey and italic
 * *Not configured*: nothing wrong here, one glance below a Setup Rail already
 * amber with "create Reel 2". Grey was defensible in the abstract — not begun is
 * not the same as got wrong — but it left the rail saying a reel was fine while
 * the step above it asked for that very reel, which is the drift CAE-1552's
 * second acceptance criterion exists to stop.
 *
 * What settled it was the asymmetry the named-but-empty fix had left behind: an
 * untouched Reel 3 drew grey, and *renaming* it — which creates the row and
 * achieves nothing else — turned it amber. The operator made the contest no
 * worse and the signal got worse. A reel the contest owes and does not have is a
 * fault; it is now drawn as one.
 */
export type ReelSlotState = 'ok' | 'warn';

export interface ReelSlot {
  reelNumber: number;
  /**
   * The operator's name for the reel, or `Reel N`.
   *
   * Unlike the Overview's section, which keeps the number *beside* the label,
   * the name replaces it here: this screen is where the name is edited, and an
   * inline-editable heading reading "Reel 2 · Both teams to score" would put
   * half of itself beyond the operator's reach. The rail's `aria-label` carries
   * the number for anything that needs to match a readiness hint to a slot.
   */
  title: string;
  /** `null` until something is added to it — the API creates reels on demand. */
  reel: ContestDetailReel | null;
  stats: ReelStats;
  /**
   * Built while the contest's reel count was higher, and stranded when it came
   * back down — see `reelFaults`.
   *
   * Carried as its own flag rather than read back out of `faults`, because the
   * detail panel needs it too and matching on a fault's wording would make the
   * sentence load-bearing. Everything the backend can do to a reel is behind
   * `assertReelNumber(reelNumber, reelCount)`, so every control on a stranded
   * reel is refused: the panel draws read-only and names the only fix there is.
   */
  beyondReelCount: boolean;
  /**
   * Everything wrong with the reel, in the operator's words — empty when there
   * is nothing. The rail prints these and `state` is derived from them, so the
   * dot cannot be amber over a reel the line beside it has nothing to say about.
   */
  faults: readonly string[];
  state: ReelSlotState;
}

/**
 * Is this reel short of the enabled options a contest cannot publish without?
 *
 * **Asked of reels that exist, which is not the same as reels with options.**
 * The question `readiness.ts` asks is `reelsByNumber.has(n)` — a reel the
 * payload carries and whose enabled count is under the threshold — and the
 * rename pencil is enough to bring one into existence: `upsertReel` INSERTs the
 * row, so naming an untouched Reel 3 creates it empty. Guarding on
 * `options > 0` instead left exactly the drift the second acceptance criterion
 * forbids: the Setup Rail turned amber with "add at least 2 enabled options to
 * Reel 3" while the rail drew the same reel grey and called it *Not
 * configured*. Observed on the live screen by renaming one. The threshold is
 * `readiness.ts`'s own, imported rather than restated.
 *
 * A slot with no reel behind it answers `false` — there are no options to be
 * short of. `reelFaults` names that one separately, the way readiness does.
 */
export const isShortOfEnabledOptions = (slot: {
  reel: ContestDetailReel | null;
  stats: ReelStats;
}): boolean => slot.reel !== null && slot.stats.enabled < MIN_ENABLED_OPTIONS_PER_REEL;

/**
 * Every way this reel is wrong, named the way the Setup Rail names it — and a
 * reel can be wrong more than one way at once, so the rail lists whichever
 * apply rather than picking one.
 *
 * **One list, read by both the dot and the line.** The colour and the sentence
 * used to be two separate reads of the same three conditions — `slotState` here
 * and `reelFaults` over in the rail — and each time a condition was added, one
 * of them was the one that got it. That is how a reel whose options all had
 * artwork but only one of them enabled came to draw a **green** dot beside a
 * Setup Rail already amber about it (caught in CAE-1552's review), and how a
 * reel nobody had created came to draw a grey one (CAE-1591). The dot is now
 * derived from this list, so a fault cannot be worded without being coloured.
 * The list is `readiness.ts`'s `reelFailures`, one entry per failure it reports.
 *
 * The order is how bad each is, worst first, which is readiness's own order too.
 * "Beyond reel N" leads because the reel should not be in the contest at all;
 * "not created" next because it does not exist yet; "needs N enabled" because it
 * blocks publishing outright; missing artwork last because it blocks publishing
 * with a thumbnail the operator can still add later. The first two are each
 * alone — see below, and a reel that does not exist has no options to be short
 * of or to want artwork — but the last two genuinely co-occur.
 */
const reelFaults = ({
  reel,
  stats,
  reelNumber,
  reelCount,
}: {
  reel: ContestDetailReel | null;
  stats: ReelStats;
  reelNumber: number;
  reelCount: number;
}): readonly string[] => {
  /*
   * **And nothing else about it.** Readiness asks the questions below only of
   * the reels the contest requires — `needingOptions` and `missingAssets` both
   * filter over `required` — so a stranded Reel 4 is named by `beyond` alone
   * there. Letting it also read "needs 2 enabled" would put the rail back to
   * flagging something the step above it does not, which is the whole point of
   * reading one list. The way out is the count back up, not two more options.
   *
   * `missingImageOptions` is the one readiness row that does walk every reel,
   * stranded ones included, so the readiness card can still offer "Open Reel 4
   * to attach an image to <pick>" against a slot this list says only `beyond`
   * about. That is deliberate on both sides: publish does refuse that option,
   * and the row above already tells the operator to put the count back, which
   * is the fix for it. The panel that link lands on says so — see
   * `beyondReelCount`, which is also why its asset control is not live.
   */
  if (reelNumber > reelCount) return [`beyond reel ${reelCount}`];

  return [
    ...(reel === null ? ['not created'] : []),
    ...(isShortOfEnabledOptions({ reel, stats })
      ? [`needs ${MIN_ENABLED_OPTIONS_PER_REEL} enabled`]
      : []),
    ...(stats.missingAssets > 0
      ? [`${stats.missingAssets} missing asset${stats.missingAssets === 1 ? '' : 's'}`]
      : []),
  ];
};

/**
 * Every reel the contest owes, plus any the payload carries beyond them — a
 * fourth reel that somehow exists is still a reel, and dropping it from the rail
 * would leave it uneditable rather than make it go away. It is drawn as a fault
 * instead (`beyond reel N`), which is what readiness calls it.
 */
export const toReelSlots = (
  reels: readonly ContestDetailReel[],
  contest: { reelCount?: number } = {},
): readonly ReelSlot[] => {
  const byNumber = new Map(reels.map((reel) => [reel.reelNumber, reel]));
  const required = requiredReelNumbers(contest);
  const numbers = [...new Set([...required, ...reels.map((reel) => reel.reelNumber)])].sort(
    (a, b) => a - b,
  );

  return numbers.map((reelNumber) => {
    const reel = byNumber.get(reelNumber) ?? null;
    const stats = reelStats(reel?.options ?? [], reel?.kind);
    const faults = reelFaults({ reel, stats, reelNumber, reelCount: required.length });

    return {
      reelNumber,
      title: reel?.label?.trim() || `Reel ${reelNumber}`,
      reel,
      stats,
      beyondReelCount: reelNumber > required.length,
      faults,
      state: faults.length > 0 ? 'warn' : 'ok',
    };
  });
};
